import React, { Fragment, memo, useMemo, useState } from 'react';
import { List, Collapse, Box } from '@mui/material';
import { OneOfListElements } from '@shared/models/project.model';

import NestedElement from './NestedElement';
import { useElementSearchString } from '@client/hooks/use-filter-elements.hooks';

import {
  GroupSectionData,
  SectionRowContentComponent,
} from '@ui/ElementList/list.model';
import { guards } from '@shared/helpers/typeguards.helpers';
import { GroupSectionRow } from './GroupSectionRow';
import { useExpandedItems } from '@client/hooks/use-expand-elements.hooks';
import { useSortGroupSections } from '@features/elements/hooks/use-sort-group-sections.hook';
import { GroupSectionMeta } from '@features/elements/element-grouping.model';
import { useElementListGroupBy } from '@features/elements/helpers/element-grouping.helpers';
import { CollapseProvider, CollapseStatus } from './collapse.context';
/**
 * Shared properties between NestedElementList and NestedElement
 */
export interface INestedListBase<
  Element extends OneOfListElements = OneOfListElements,
> {
  indentation?: number;

  /**
   * If true this list will be rendered in select mode (for buildingVersions)
   */
  selectMode?: boolean;

  /**
   * Provide hook to get sorted and filtered children.
   * This function will be different for each element type.
   * If not provided, the default function will be used.
   */
  useFilterSortChildren?: (element?: Element) => Element[];

  /**
   * Leave off-screen rows unmounted until they intersect the viewport.
   * Intended for large flat lists such as the project selector.
   */
  cullBeforeMount?: boolean;

  /**
   * Reserved height of each {@link HideOutsideViewport} slot.
   * Defaults to the element-list row height. Pass a taller value when the
   * rendered row is taller, so culled placeholders still overflow the list.
   */
  rowHeight?: number;
}

export type GroupSectionsBuilder<SectionMeta = unknown> = (
  root: OneOfListElements[],
) => GroupSectionData<OneOfListElements, SectionMeta>[];

type NestedElementListGroupingProps = {
  /**
   * Optional grouping for root level only. When provided, the list will render sections at indentation 0.
   */
  groupSectionBuilder: GroupSectionsBuilder<GroupSectionMeta>;
  /**
   * Optional component to fully control the section header content while keeping
   * collapse/expand state handled internally.
   */
  RowContentComponent?: SectionRowContentComponent<
    OneOfListElements,
    GroupSectionMeta
  >;
  /** When set, double-clicking a section row opens Focus View for that group. */
  onSectionDoubleClick?: (sectionKey: string) => void;
};

type NestedElementListProps<Item extends OneOfListElements> = {
  elements: Item[];
  expanded?: boolean;
  groupProps?: NestedElementListGroupingProps;
};

type INestedElementListInput<Item extends OneOfListElements> = INestedListBase &
  NestedElementListProps<Item>;

const NestedElementList = <T extends OneOfListElements>({
  elements,
  expanded,
  indentation = 0,
  selectMode,
  useFilterSortChildren,
  cullBeforeMount,
  rowHeight,
  groupProps,
}: INestedElementListInput<T>) => {
  const [collapseStatus, setCollapseStatus] = useState<CollapseStatus>(
    expanded === false ? 'exited' : 'entered',
  );
  const collapseContextValue = useMemo(
    () => ({
      status: collapseStatus,
      setStatus: setCollapseStatus,
    }),
    [collapseStatus],
  );
  // Root-level grouping
  if (indentation === 0 && guards.is.defined(groupProps)) {
    return (
      <CollapseProvider value={collapseContextValue}>
        <GroupedRoot
          sections={groupProps.groupSectionBuilder(elements)}
          selectMode={selectMode}
          useFilterSortChildren={useFilterSortChildren}
          cullBeforeMount={cullBeforeMount}
          rowHeight={rowHeight}
          RowContentComponent={groupProps.RowContentComponent}
          onSectionDoubleClick={groupProps.onSectionDoubleClick}
        />
      </CollapseProvider>
    );
  }

  const list = (
    <List sx={noPadding} disablePadding dense>
      {elements.map((element) => (
        <NestedElement
          key={element.id}
          element={element}
          indentation={indentation}
          selectMode={selectMode}
          useFilterSortChildren={useFilterSortChildren}
          cullBeforeMount={cullBeforeMount}
          rowHeight={rowHeight}
        />
      ))}
    </List>
  );

  // Collapse clips to its animated height. Culled placeholders must keep the
  // full list height so the parent can overflow and show a scrollbar.
  if (cullBeforeMount && expanded !== false) {
    return (
      <CollapseProvider value={collapseContextValue}>{list}</CollapseProvider>
    );
  }

  return (
    <CollapseProvider value={collapseContextValue}>
      <Collapse
        in={expanded !== false}
        onExited={() => setCollapseStatus('exited')}
        onExiting={() => setCollapseStatus('exiting')}
        onEntering={() => setCollapseStatus('entering')}
        onEntered={() => setCollapseStatus('entered')}
        timeout="auto"
        unmountOnExit
      >
        {list}
      </Collapse>
    </CollapseProvider>
  );
};

const noPadding = {
  paddingTop: '0 !important',
  paddingBottom: '0 !important',
};

export default memo(NestedElementList);

// === Root grouping components ===

// this must be in this file, because it renders NestedElementList recursively
// Don't want circular imports, so this is here
const GroupedRoot: React.FC<
  INestedListBase & {
    sections: GroupSectionData<OneOfListElements, GroupSectionMeta>[];
    RowContentComponent?: SectionRowContentComponent<
      OneOfListElements,
      GroupSectionMeta
    >;
    onSectionDoubleClick?: (sectionKey: string) => void;
  }
> = ({
  sections,
  selectMode,
  useFilterSortChildren,
  cullBeforeMount,
  rowHeight,
  RowContentComponent,
  onSectionDoubleClick,
}) => {
  const [searchString] = useElementSearchString();
  const isSearching = guards.is.nonEmptyString(searchString);
  const [groupById] = useElementListGroupBy();
  const sortedSections = useSortGroupSections(sections);
  const expandedItems = useExpandedItems({ defaultPrefix: groupById });

  return (
    <Fragment>
      {sortedSections.map((section) => (
        <Box key={section.key}>
          <GroupSectionRow
            section={section}
            expanded={expandedItems.is(section.key)}
            onExpandToggle={
              isSearching
                ? undefined
                : (next) => expandedItems.set(section.key, next)
            }
            SectionRowContent={RowContentComponent}
            onDoubleClick={() => onSectionDoubleClick?.(section.key)}
          >
            <NestedElementList
              elements={section.items}
              expanded={isSearching || expandedItems.is(section.key)}
              indentation={1}
              selectMode={selectMode}
              useFilterSortChildren={useFilterSortChildren}
              cullBeforeMount={cullBeforeMount}
              rowHeight={rowHeight}
            />
          </GroupSectionRow>
        </Box>
      ))}
    </Fragment>
  );
};
